/**
 * UnpluggedWidget
 * Visar urkopplade fordon med möjlighet att markera och tilldela.
 * Röd = ej tilldelad, Gul = tilldelad (visar tilldelad persons namn).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from '@/store/languageStore';
import api from '@/services/api/client';
import { assignmentApi } from '@/services/assignments/assignmentApi';
import {
  BoltSlashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowPathIcon,
  UserPlusIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import { DashboardVehicleAssignModal } from './DashboardVehicleAssignModal';
import { EventLocationMapModal } from '@/components/maps/EventLocationMapModal';
import { ROUTES } from '@/constants/routes';
import VehicleRegistrationLink from './VehicleRegistrationLink';

interface UnpluggedVehicle {
  vehicleId: number;
  registrationNumber: string;
  make?: string;
  model?: string;
  organizationName?: string;
  unpluggedTimestamp?: string;
  unpluggedAddress?: string;
  unpluggedLat?: number | null;
  unpluggedLng?: number | null;
}

interface VehicleAssignment {
  assignmentId: number;
  assignedToName: string;
  status: string;
}

function unpluggedWhen(value: string | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  try {
    return date.toLocaleString('sv-SE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return null;
  }
}

const UnpluggedWidget: React.FC<{ startExpanded?: boolean; onRemove?: () => void }> = ({ startExpanded = false }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(startExpanded);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [assignmentMap, setAssignmentMap] = useState<Record<number, VehicleAssignment>>({});
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [mapVehicle, setMapVehicle] = useState<UnpluggedVehicle | null>(null);

  const { data: responseData, isLoading: loading, refetch } = useQuery(
    ['dashboard-widget', '/api/dashboard/data/unplugged-vehicles'],
    async () => {
      const response = await api.get('/api/dashboard/data/unplugged-vehicles');
      if (response.data.success) return response.data.data as UnpluggedVehicle[];
      throw new Error(response.data.error);
    },
    { staleTime: 60 * 1000 },
  );

  const vehicles = Array.isArray(responseData) ? responseData : [];

  const loadAssignments = useCallback(async () => {
    if (vehicles.length === 0) return;
    try {
      const ids = vehicles.map(v => v.vehicleId);
      const map = await assignmentApi.getOpenByVehicleIds(ids, 'UNPLUGGED_DEVICE');
      setAssignmentMap(map);
    } catch {
      // silent
    }
  }, [vehicles]);

  useEffect(() => { loadAssignments(); }, [loadAssignments]);

  const toggleSelect = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    const unassignedIds = vehicles.filter(v => !assignmentMap[v.vehicleId]).map(v => v.vehicleId);
    if (unassignedIds.every(id => selected.has(id))) {
      setSelected(new Set());
    } else {
      setSelected(new Set(unassignedIds));
    }
  };

  const unassignedCount = vehicles.filter(v => !assignmentMap[v.vehicleId]).length;
  const assignedCount = vehicles.length - unassignedCount;

  const handleAssigned = () => {
    setShowAssignModal(false);
    setSelected(new Set());
    refetch();
    loadAssignments();
  };

  if (loading && vehicles.length === 0) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-500" />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div
        className="flex items-center justify-between cursor-pointer hover:bg-bgHover rounded-lg p-2 -m-2"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <BoltSlashIcon className="w-8 h-8 text-red-500" />
          <div>
            <div className="text-2xl font-bold text-textDefault">{vehicles.length}</div>
            <div className="text-xs text-textSecondary">{t('unpluggedWidget.subtitle')}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {unassignedCount > 0 && (
            <span className="text-xs font-medium px-2 py-1 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
              {t('unpluggedWidget.warning')}
            </span>
          )}
          {assignedCount > 0 && (
            <span className="text-xs font-medium px-2 py-1 rounded-full bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300">
              {assignedCount} {t('assignments.assigned')}
            </span>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); refetch(); loadAssignments(); }}
            className="p-1 hover:bg-bgElevated rounded"
            title={t('common.refresh')}
          >
            <ArrowPathIcon className={`w-4 h-4 text-textSecondary ${loading ? 'animate-spin' : ''}`} />
          </button>
          {expanded ? <ChevronUpIcon className="w-5 h-5 text-textSecondary" /> : <ChevronDownIcon className="w-5 h-5 text-textSecondary" />}
        </div>
      </div>

      {/* Expanded list */}
      {expanded && vehicles.length > 0 && (
        <div className="mt-3 pt-3 border-t border-borderDefault">
          {/* Toolbar */}
          <div className="flex items-center justify-between mb-2 px-1">
            <label className="flex items-center gap-2 text-xs text-textSecondary cursor-pointer select-none">
              <input
                type="checkbox"
                checked={unassignedCount > 0 && vehicles.filter(v => !assignmentMap[v.vehicleId]).every(v => selected.has(v.vehicleId))}
                onChange={toggleAll}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              {t('assignments.selectAll')} ({unassignedCount})
            </label>
            {selected.size > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); setShowAssignModal(true); }}
                className="flex items-center gap-1 px-3 py-1 text-xs font-medium bg-primary text-white rounded-lg hover:bg-primary/90"
              >
                <UserPlusIcon className="w-3.5 h-3.5" />
                {t('assignments.assignSelected')} ({selected.size})
              </button>
            )}
          </div>

          {/* Vehicle list */}
          <div className={startExpanded ? 'max-h-[60vh] overflow-y-auto space-y-1' : 'max-h-64 overflow-y-auto space-y-1'}>
            {vehicles.map(vehicle => {
              const assignment = assignmentMap[vehicle.vehicleId];
              const isAssigned = !!assignment;
              const isSelected = selected.has(vehicle.vehicleId);
              const when = unpluggedWhen(vehicle.unpluggedTimestamp);

              return (
                <div
                  key={vehicle.vehicleId}
                  className={`py-1.5 px-2 rounded text-sm flex items-start gap-2 ${
                    isAssigned
                      ? 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200'
                      : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                  }`}
                >
                  {!isAssigned && (
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelect(vehicle.vehicleId)}
                      className="mt-1 rounded border-gray-300 text-primary focus:ring-primary flex-shrink-0"
                    />
                  )}
                  {isAssigned && <div className="w-4 flex-shrink-0" />}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <VehicleRegistrationLink
                          registrationNumber={vehicle.registrationNumber}
                          vehicleId={vehicle.vehicleId}
                          className="font-medium"
                        />
                        {typeof vehicle.make === 'string' && vehicle.make && (
                          <span className={`text-xs ${isAssigned ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-500'}`}>
                            {vehicle.make}
                          </span>
                        )}
                      </div>
                      {when && (
                        <span className={`text-xs shrink-0 ${isAssigned ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'}`}>
                          {when}
                        </span>
                      )}
                    </div>
                    <div className={`text-xs truncate ${isAssigned ? 'text-yellow-600/90 dark:text-yellow-400/90' : 'text-red-600/90 dark:text-red-400/90'}`}
                      title={typeof vehicle.unpluggedAddress === 'string' ? vehicle.unpluggedAddress : undefined}
                    >
                      {typeof vehicle.unpluggedAddress === 'string' && vehicle.unpluggedAddress
                        ? vehicle.unpluggedAddress
                        : t('dashboard.addressUnavailable')}
                    </div>
                    {isAssigned && (
                      <div className="text-xs font-medium text-yellow-700 dark:text-yellow-300 mt-0.5">
                        → {assignment.assignedToName}
                      </div>
                    )}
                    {vehicle.unpluggedLat != null && vehicle.unpluggedLng != null && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMapVehicle(vehicle);
                        }}
                        className="mt-0.5 inline-flex items-center gap-1 text-xs text-primary hover:underline"
                      >
                        <MapPinIcon className="h-3.5 w-3.5" />
                        {t('booking.vehicleMapShow')}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {expanded && (
        <div className="mt-2 px-1">
          <Link to={ROUTES.FLEET_UNPLUG_EVENTS} className="text-xs text-primary hover:underline">
            {t('unplugEvents.openHistory')}
          </Link>
        </div>
      )}

      {/* Empty state */}
      {expanded && vehicles.length === 0 && (
        <div className="mt-3 pt-3 border-t border-borderDefault text-center text-green-600 text-sm py-4">
          ✓ {t('unpluggedWidget.allConnected')}
        </div>
      )}

      {/* Assign Modal */}
      {mapVehicle && mapVehicle.unpluggedLat != null && mapVehicle.unpluggedLng != null && (
        <EventLocationMapModal
          title={mapVehicle.registrationNumber}
          subtitle={t('unplugEvents.unplugged')}
          lat={mapVehicle.unpluggedLat}
          lng={mapVehicle.unpluggedLng}
          occurredAt={mapVehicle.unpluggedTimestamp}
          address={mapVehicle.unpluggedAddress}
          onClose={() => setMapVehicle(null)}
        />
      )}

      {showAssignModal && (
        <DashboardVehicleAssignModal
          items={vehicles.filter(v => selected.has(v.vehicleId)).map(v => ({
            vehicleId: v.vehicleId,
            registrationNumber: v.registrationNumber,
            title: `${t('unpluggedWidget.title')}: ${v.registrationNumber}`,
          }))}
          defaultCategoryCode="UNPLUGGED_DEVICE"
          onClose={() => setShowAssignModal(false)}
          onAssigned={handleAssigned}
        />
      )}
    </div>
  );
};

export default UnpluggedWidget;
